import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IdleService, NotificationService } from '@app/core';
import { CommonService } from '@app/core/common.service';
import { AppProfileData, SideNavProfileData } from '@app/models';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { GENERAL_MESSAGES, SNACKBAR_ACTIONS } from '../data/messages';
import { APP_PLAN_TIERS_NAME, AppBreakPoints, FREEMIUM_TIER_DISABLED_URLS, SNACK_TIMEOUT } from '../data/values';
import { IntercomService } from '@app/core/intercom.service';

@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    standalone: false
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {

  appProfileData!: AppProfileData;
  sideNavData!: SideNavProfileData;
  screenStatus: string | null = null;
  statusMessage: string | null = null;

  sidePanel: boolean = false;
  hasServiceDownWarns: boolean = false;
  readonly unsubscribeAll$ = new Subject();
  public windowInnerWidth: any;

  constructor(
    private readonly _commonService: CommonService, 
    private readonly _router: Router, 
    private readonly _notificationService: NotificationService, 
    private readonly _intercomService: IntercomService,
    private readonly _idleService: IdleService
  ) {    
    this._commonService.getServiceDownBanner()
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(
        response => {
          if (response.length) {
            this.hasServiceDownWarns = response.some(rec => !!rec.is_service_down);
            this._commonService.setServiceDownBanner(response);
          }
        }
      )
  }

  ngAfterViewInit() {
    if (!this.appProfileData?.user.document_accepted && !this._router.url.includes('/home/broadcast')) {
        this._router.navigate(['/home/broadcast']);
    }
    this._commonService.isSidePanelOpen
      .subscribe(value => {
        this.sidePanel = value;
      });
    this._commonService.setSidebarLoading(true);
    this._commonService.currentAccountLimitsData
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(acLimits => {
        if (acLimits?.plan == APP_PLAN_TIERS_NAME.FREE) {
          this.sideNavData = new SideNavProfileData(this.appProfileData);
          this.sideNavData = {
            ...this.sideNavData, 
            restrictedModules: [...this.sideNavData.restrictedModules, ...FREEMIUM_TIER_DISABLED_URLS.map(url => `/home/${url}`)]
          };
        }
        this._commonService.setSidebarLoading(false);
      });
    this.checkNotifyMobileVersion();
  }

  ngOnInit() {
    this._idleService.startWatching();
    this._commonService.appProfileDataAsObservable
    .pipe(
        filter(appData => (!!appData?.currentOrganization && appData.currentOrganization?.id >= 0)),
        takeUntil(this.unsubscribeAll$))
      .subscribe(appData => {
        let clonedRestrictedURLS: string[] = [];
        if ((this.appProfileData?.currentOrganization?.id == appData.currentOrganization?.id) && this.sideNavData?.restrictedModules?.length) {
          clonedRestrictedURLS = [...this.sideNavData.restrictedModules];
        }
        this.appProfileData = appData;
        this.sideNavData = new SideNavProfileData(appData);
        if (clonedRestrictedURLS?.length) {
          this.sideNavData = { ...this.sideNavData, restrictedModules: clonedRestrictedURLS };
        }
        if (appData.user.document_accepted) {
          this.checkNotifyMobileVersion();
        }
        this.fetchOrganizations();
      });
    this._commonService.screenStatus
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(status => {
        this.screenStatus = status?.status || null;
        this.statusMessage = status?.message || null;
      });
    
    this._router.events.subscribe(event => {
      if (this.screenStatus && event instanceof NavigationEnd) {
        this._commonService.setScreenStatus(null);
      }
      this._intercomService.updateData();
    })
    this.windowInnerWidth = window.innerWidth;
  }
  
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.windowInnerWidth = window.innerWidth;
    this.checkNotifyMobileVersion();
  }

  ngOnDestroy() {
    this.unsubscribeAll$.next(null);
    this.unsubscribeAll$.complete();
  }

  checkNotifyMobileVersion() {
    const bodyElement = document.querySelector('body');
    if (this.isMobileView) {
      bodyElement?.classList.add('mobile-version-body');
      let isSameURL = !location.href.toString().includes('/home/broadcast/sendBroadcast');
      this._router.navigate(['/home/broadcast/sendBroadcast']).then(() => {
        if (isSameURL) {
            this._notificationService.showSuccess(GENERAL_MESSAGES.MOBILE_VERSION_MSG, SNACKBAR_ACTIONS.OK, { panelClass: 'full-vw', duration: SNACK_TIMEOUT })
          }
      });
    } else if (bodyElement?.classList.contains('mobile-version-body')) {
      bodyElement?.classList.remove('mobile-version-body');
    }
  }

  private fetchOrganizations(): void {
    this._commonService.fetchOrgSelectorList()
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe();
  }

  get isMobileView() {
    return this._commonService.mobileVersionVFeatureFlag && (this.windowInnerWidth <= AppBreakPoints.mobile) && !!this.appProfileData.user.document_accepted;
  }

}
